import {
  useTimeOffListQuery,
  useTimeOffUserListShortQuery,
} from '@common/cache';

import { ITimeOffTableEntry } from './useTimeOffTable';
import { useTimeOffTableStore, ITimeOffTableFilters } from '@store';
import { isEqual } from 'lodash';
import { checkUserPermission } from 'common/auth';
import useUserStore from 'store/user.store.ts';
import dayjs from 'dayjs';

const initialFilter: ITimeOffTableFilters = {
  sort_order: 'desc',
  sort_by: 'user',
  page: 1,
  limit: 100,
  only_upcoming: false,
  start_date: '',
  end_date: '',
  user_ids: [],
};

export const useTimeOff = () => {
  const filters = useTimeOffTableStore(s => s.filters);
  const setFilters = useTimeOffTableStore(s => s.setFilters);
  const { user } = useUserStore();
  const manageTimeOffPermission = checkUserPermission(user, 'manage_time_off');

  const { data: userOptions = [] } = useTimeOffUserListShortQuery({});

  // When "Only upcoming" is ON and a range is set, clamp the effective start
  // date to today so past items are excluded even if the range includes them.
  const effectiveFilters: ITimeOffTableFilters = (() => {
    if (filters.only_upcoming && filters.start_date) {
      const today = dayjs().format('YYYY-MM-DD');
      const clampedStart =
        filters.start_date < today ? today : filters.start_date;
      return { ...filters, start_date: clampedStart };
    }
    return filters;
  })();

  const { data = { users: [], count: 0 }, isLoading } =
    useTimeOffListQuery(effectiveFilters);

  const entries: ITimeOffTableEntry[] = data.users;

  const handleSearchUserIdChange = (newValues: string[]) => {
    handleFilterChange({ user_ids: newValues });
  };

  const handleOwnTasksOnlyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ only_upcoming: e.target.checked });
  };

  const handleFilterChange = (newFilters: Partial<ITimeOffTableFilters>) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters(initialFilter);
  };

  const isFiltersEmpty =
    !filters.only_upcoming &&
    !filters.start_date &&
    !filters.end_date &&
    !filters.user_ids?.length;

  return {
    userOptions: userOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    entries,
    count: data.count,
    isLoading,
    filters,
    handleFilterChange,
    handleSearchUserIdChange,
    handleOwnTasksOnlyChange,
    handleResetFilters,
    isFiltersEmpty,
    isInitialFilters: isEqual(initialFilter, {
      ...filters,
      start_date: filters.start_date || '',
      end_date: filters.end_date || '',
    }),
    manageTimeOffPermission,
    setFilters,
  };
};
